import React, { useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { FaAndroid, FaApple, FaTimes } from "react-icons/fa";
import Logo from "../assets/images/logo-black.png";

const ANDROID_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.zamzam.restaurantapp";
const IOS_STORE_URL =
  "https://apps.apple.com/in/app/my-zamzam-app/id6752317913";
const APP_URL_SCHEME = "myzamzamapp://";
const STORE_FALLBACK_MS = 1500;
const SESSION_KEY = "MOBILE_APP_POPUP_DISMISSED";

const detectPlatform = () => {
  if (typeof navigator === "undefined") {
    return { isMobile: false, isIOS: false, isAndroid: false };
  }
  const ua = (navigator.userAgent || "").toLowerCase();

  const isIOS =
    /iphone|ipad|ipod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /android/.test(ua);
  const isTabletUA = /tablet|ipad|playbook|silk/.test(ua);
  const isMobileUA = /mobile|iphone|ipod|android/.test(ua);
  const hasTouchTablet =
    navigator.maxTouchPoints > 1 &&
    window.innerWidth >= 600 &&
    window.innerWidth <= 1024;

  const isMobile =
    isIOS || isAndroid || isMobileUA || isTabletUA || hasTouchTablet;

  return { isMobile, isIOS, isAndroid };
};

const MobileAppPopup = () => {
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState({ isIOS: false, isAndroid: false });
  const fallbackTimerRef = useRef(null);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(SESSION_KEY) === "1") return;
    } catch (e) {}

    const p = detectPlatform();
    if (p.isMobile && (p.isIOS || p.isAndroid)) {
      setPlatform(p);
      setShow(true);
    }
  }, []);

  const dismiss = () => {
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch (e) {}
    setShow(false);
  };

  // Try the deep link; if the app didn't take over the page within
  // STORE_FALLBACK_MS, redirect to the appropriate store.
  const openAppOrStore = () => {
    const storeUrl = platform.isIOS ? IOS_STORE_URL : ANDROID_STORE_URL;

    const clearFallback = () => {
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("blur", onPageHide);
    };

    const onVisibilityChange = () => {
      if (document.hidden) clearFallback();
    };
    const onPageHide = () => clearFallback();

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("blur", onPageHide);

    fallbackTimerRef.current = setTimeout(() => {
      clearFallback();
      window.location.href = storeUrl;
    }, STORE_FALLBACK_MS);

    if (platform.isAndroid) {
      // Android Chrome handles intent:// URLs with a built-in store fallback,
      // but we keep the JS timer for browsers that don't (Firefox, in-app WebViews).
      window.location.href = APP_URL_SCHEME;
    } else {
      window.location.href = APP_URL_SCHEME;
    }

    dismiss();
  };

  useEffect(() => {
    return () => {
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    };
  }, []);

  if (!show) return null;

  const PlatformIcon = platform.isIOS ? FaApple : FaAndroid;
  const deviceLabel = platform.isIOS ? "iPhone/iPad" : "Android device";

  return (
    <Modal
      show={show}
      onHide={dismiss}
      centered
      backdrop="static"
      keyboard={false}
      aria-labelledby="mobile-app-popup-title"
    >
      <Modal.Header className="border-0 pb-0 position-relative">
        <Modal.Title
          id="mobile-app-popup-title"
          className="w-100 text-center fw-semibold"
        >
          Get a better experience
        </Modal.Title>
        <button
          type="button"
          aria-label="Close"
          onClick={dismiss}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 32,
            height: 32,
            borderRadius: "50%",
            border: "none",
            background: "#f1f1f1",
            color: "#333",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            padding: 0,
            lineHeight: 1,
          }}
        >
          <FaTimes size={14} />
        </button>
      </Modal.Header>

      <Modal.Body className="text-center">
        <img
          src={Logo}
          alt="My Zamzam"
          style={{ height: 56, marginBottom: 14 }}
        />
        <p className="mb-4 text-muted">
          Continue with the My Zamzam app for a faster, smoother experience on
          your {deviceLabel}.
        </p>

        <div className="d-flex flex-column gap-2">
          <Button
            size="lg"
            className="d-flex align-items-center justify-content-center gap-2"
            style={{
              backgroundColor: "#f9bc03",
              border: "none",
              color: "#fff",
              borderRadius: 20,
            }}
            onClick={openAppOrStore}
          >
            <PlatformIcon size={20} />
            Open in App
          </Button>

          <Button variant="link" className="text-muted" onClick={dismiss}>
            Continue on web
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default MobileAppPopup;
